/**
 * This file is for defining common selectors that can be used across the app.
 */

import { useAppSelector } from 'src/utils/Hooks/typedHooks';
import _get from 'lodash/get';
import ReduxStore from 'src/types/store/reduxStore';
import { shouldShowNewAtlas } from 'src/utils/app';
import { buildAppUrl } from 'src/components/common/Logo/Logo';
import { encodeHash } from 'src/utils/hashIds';
import { createSelector } from '@reduxjs/toolkit';
import { AMAZON_RETAILER_IDS } from 'src/store/modules/retailer/reducers';

export const allSegmentsSelector = createSelector(
  (state) => state.segments,
  (segments) => [...segments.mySegments, ...segments.teamSegments]
);

/**
 * Return a combined list of user created segments and team segments
 */
export const useAllSegments = () => {
  return useAppSelector(allSegmentsSelector);
};

export const allSearchTermListsSelector = createSelector(
  (state) => state.segments,
  (segments) => [...segments.teamSearchTermLists, ...segments.mySearchTermLists]
);

/**
 * Return a combined list of user created search term lists and team search term lists
 */
export const useAllSearchTermLists = () => {
  return useAppSelector(allSearchTermListsSelector);
};

export const allSegmentsAndSearchTermListsSelector = createSelector(
  (state) => state.segments,
  (segments) => {
    const { mySegments, teamSegments, teamSearchTermLists, mySearchTermLists } = segments;
    return [...mySegments, ...teamSegments, ...teamSearchTermLists, ...mySearchTermLists];
  }
);

/**
 * Return a combined list of user created segments and team segments and user created search term lists and team search term lists
 */
export const useAllSegmentsAndSearchTermLists = () => {
  return useAppSelector(allSegmentsAndSearchTermListsSelector);
};

/**
 * Get the main entity type
 */
export const useMainEntityType = () => {
  return useAppSelector((state) => state?.entityService?.mainEntity?.type);
};

/**
 * Get the main entity name
 */
export const useMainEntityName = () => {
  return useAppSelector((state) => state?.entityService?.mainEntity?.name);
};

/**
 * Get the user id
 */
export const useUserId = () => {
  return useAppSelector((state) => _get(state, ['user', 'session', 'userId']));
};

/**
 * Get the main entity id
 */
export const useMainEntityId = () => {
  return useAppSelector((state) => state.entityService?.mainEntity?.id);
};

export const useMainEntity = () => {
  return useAppSelector((state) => state.entityService.mainEntity);
};

/**
 * Get the retailer id
 */
export const useRetailerId = () => {
  return useAppSelector((state) => _get(state, ['retailer', 'id']));
};

export const useRetailerSku = () => {
  return useAppSelector((state) => state.entityService?.mainEntity?.retailerSku);
};

/**
 * Get the retailer display name
 */
export const useRetailerDisplayName = () => {
  const retailerId = useRetailerId();
  const availableRetailers = useAppSelector((state) => state.retailer.availableRetailers);
  const retailers = availableRetailers;
  const retailerObj = retailers.find((r) => r.id === retailerId);
  if (retailerObj) {
    return retailerObj.displayName;
  }

  // Default, return retailer Id if no match found
  return retailerId;
};

/**
 * Get the beacon client id]
 */
export const useBeaconClientId = () => {
  return useAppSelector((state) => _get(state, ['user', 'config', 'vendor', 'BeaconClientId']));
};

export const useCompanyId = () => {
  return useAppSelector((state) => _get(state, ['user', 'session', 'companyId']));
};

export const useDivisionId = () => {
  return useAppSelector((state) => _get(state, ['user', 'session', 'divisionId']));
};

export const useUserEmail = () => {
  return useAppSelector((state) => _get(state, ['user', 'session', 'email']));
};

/**
 * Returns a boolean indicating if the main entity is a product and the product entity if it exists
 */
export const useProductEntity = (): {
  productEntity: ReduxStore['entityService']['mainEntity'] | null;
  isProductEntity: boolean;
} => {
  return useAppSelector((state) => {
    if (_get(state, ['entityService', 'mainEntity', 'type']) === 'product') {
      return { productEntity: _get(state, ['entityService', 'mainEntity'], null), isProductEntity: true };
    }

    return { productEntity: null, isProductEntity: false };
  });
};
/**
 * Returns a boolean indicating if the main entity is a category and the category entity if it exists
 */
export const useCategoryEntity = (): {
  categoryEntity: ReduxStore['entityService']['mainEntity'] | null;
  isCategoryEntity: boolean;
  isAllCategoriesEntity: boolean;
} => {
  return useAppSelector((state) => {
    if (_get(state, ['entityService', 'mainEntity', 'type']) === 'category') {
      return {
        categoryEntity: _get(state, ['entityService', 'mainEntity'], null),
        isCategoryEntity: true,
        isAllCategoriesEntity: _get(state, ['entityService', 'mainEntity', 'id']) === '0'
      };
    }

    return { categoryEntity: null, isCategoryEntity: false, isAllCategoriesEntity: false };
  });
};
/**
 * Returns a boolean indicating if the main entity is a subcategory and the subcategory entity if it exists
 */
export const useSubcategoryEntity = (): {
  subcategoryEntity: ReduxStore['entityService']['mainEntity'] | null;
  isSubcategoryEntity: boolean;
} => {
  return useAppSelector((state) => {
    if (_get(state, ['entityService', 'mainEntity', 'type']) === 'subcategory') {
      return { subcategoryEntity: _get(state, ['entityService', 'mainEntity'], null), isSubcategoryEntity: true };
    }

    return { subcategoryEntity: null, isSubcategoryEntity: false };
  });
};

export const useTrendEntity = (): {
  trendEntity: ReduxStore['entityService']['mainEntity'] | null;
  isTrendEntity: boolean;
} => {
  return useAppSelector((state) => {
    if (_get(state, ['entityService', 'mainEntity', 'type']) === 'microtrend') {
      return { trendEntity: _get(state, ['entityService', 'mainEntity'], null), isTrendEntity: true };
    }

    return { trendEntity: null, isTrendEntity: false };
  });
};

export const useBrandEntity = (): {
  brandEntity: ReduxStore['entityService']['mainEntity'] | null;
  isBrandEntity: boolean;
} => {
  return useAppSelector((state) => {
    if (_get(state, ['entityService', 'mainEntity', 'type']) === 'brand') {
      return { brandEntity: _get(state, ['entityService', 'mainEntity'], null), isBrandEntity: true };
    }

    return { brandEntity: null, isBrandEntity: false };
  });
};

export const useSegmentEntity = (): {
  segmentEntity: ReduxStore['entityService']['mainEntity'] | null;
  isSegmentEntity: boolean;
} => {
  return useAppSelector((state) => {
    if (_get(state, ['entityService', 'mainEntity', 'type']) === 'segment') {
      return { segmentEntity: _get(state, ['entityService', 'mainEntity'], null), isSegmentEntity: true };
    }

    return { segmentEntity: null, isSegmentEntity: false };
  });
};

export const useBusinessUnitEntity = (): {
  businessUnitEntity: ReduxStore['entityService']['mainEntity'] | null;
  isBusinessUnitEntity: boolean;
} => {
  return useAppSelector((state) => {
    if (_get(state, ['entityService', 'mainEntity', 'type']) === 'businessunit') {
      return { businessUnitEntity: _get(state, ['entityService', 'mainEntity'], null), isBusinessUnitEntity: true };
    }

    return { businessUnitEntity: null, isBusinessUnitEntity: false };
  });
};

/**
 * When on the topic view, gets the name of the company
 * that owns the topic
 */
export const useCompanyName = (): string => {
  const companyName = useAppSelector((state) => state?.user?.config?.vendor?.CompanyName);

  return companyName;
};

export const useUserProfile = () => {
  return useAppSelector((state) => state.user?.config?.profile);
};

export const useAppSubscribed = (): {
  [appName: string]: {
    id: string;
    path: string;
    shouldBeEnabled: () => boolean;
    getLabel: () => string;
  };
} => {
  const isRestrictedDemoUser = useAppSelector((state) => state.user.config.isRestrictedDemoUser);
  const app = useAppSelector((state) => state.app);
  const retailer = useAppSelector((state) => state.retailer);
  const user = useAppSelector((state) => state.user);
  const homeMap = {
    atlas: isRestrictedDemoUser
      ? `/home&tab=summary&subtab=atlas&entityType=category`
      : `/company/${encodeHash(0)}?rid=${retailer.id}`,
    advertising: `/overview&tab=${app.defaultQueryParams.tab}&subtab=${app.defaultQueryParams.subtab}`,
    omni: `/client/${user.config.vendor.BeaconClientId}`,
    beacon: ''
  };
  const appStage = useAppSelector((state) => state.app.stage);

  const APP_DETAILS = {
    advisor: {
      id: 'advisor',
      path: buildAppUrl('advisor', '/', appStage),
      shouldBeEnabled: () => {
        return true;
      },
      getLabel: () => {
        return 'Advisor';
      }
    },
    atlas: {
      id: 'atlas',
      path: buildAppUrl('atlas', homeMap.atlas, appStage),
      shouldBeEnabled: () => {
        return true;
      },
      getLabel: () => {
        return 'Atlas';
      }
    },
    beacon: {
      id: 'beacon',
      path: buildAppUrl('beacon', homeMap.beacon, appStage),
      shouldBeEnabled: () => {
        return true;
      },
      getLabel: () => {
        return 'Beacon';
      }
    },
    admanager: {
      id: 'admanager',
      path: buildAppUrl('admanager', '/', appStage),
      shouldBeEnabled: () => {
        return true;
      },
      getLabel: () => {
        return 'Ad Manager';
      }
    },
    omni: {
      id: 'omni',
      path: buildAppUrl('omni', homeMap.omni, appStage),
      shouldBeEnabled: () => {
        return true;
      },
      getLabel: () => {
        return 'Omni';
      }
    },
    shopperos: {
      id: 'shopperos',
      path: buildAppUrl('shopperos', '/', appStage),
      shouldBeEnabled: () => {
        return true;
      },
      getLabel: () => {
        return 'Shopper OS';
      }
    },
    shopperanalytics: {
      id: 'shopperanalytics',
      // Shopper Analytics has no -dev deployment; non-prod stages map to -beta
      path:
        appStage === 'prod' ? 'https://shopperanalytics.stackline.com/' : 'https://shopperanalytics-beta.stackline.com/',
      shouldBeEnabled: () => {
        return true;
      },
      getLabel: () => {
        return 'Shopper Analytics';
      }
    }
    // digitalshelf: {
    //   id: 'digitalshelf',
    //   path: buildAppUrl('digital-shelf', '/', appStage),
    //   shouldBeEnabled: () => {
    //     return true;
    //   },
    //   getLabel: () => {
    //     return 'Digital Shelf';
    //   }
    // }
  };

  return APP_DETAILS;
};

/** For Atlas this will include all brands 0 - */
export const useBrandsFollowingIds = (): number[] => {
  const brands = useAppSelector((state) => state?.brandsFollowing);
  return brands.map((brand) => brand.id).filter((brand) => brand !== 0);
};

export const categoryIdsSelector = createSelector(
  (state) => state.categories,
  (categories) => categories.map((category) => category.id)
);

export const categoriesByRetailerIdSelector = createSelector(
  [(state) => state.retailer, (state) => state.categoriesByRetailerId],
  (retailer, categoriesByRetailerId) => categoriesByRetailerId[retailer.id]?.map((category) => category.id)
);

/**
 * Hook to check if categories have been loaded.
 * This helps prevent race conditions where requests are made before categories are available.
 */
export const useAreCategoriesLoaded = (): boolean => {
  const categories = useAppSelector((state) => state.categories);
  const categoriesByRetailerId = useAppSelector(categoriesByRetailerIdSelector);

  if (shouldShowNewAtlas()) {
    // For Atlas, check if categoriesByRetailerId is loaded for the current retailer
    return categoriesByRetailerId !== undefined && categoriesByRetailerId.length > 0;
  }

  // For Beacon, check if categories array is populated
  return categories !== undefined && categories.length > 0;
};

/**
 * Hook to check if the current segment/searchtermlist entity is loaded and available.
 * This prevents race conditions where requests are made before segment data is available.
 */
export const useIsCurrentSegmentLoaded = (): boolean => {
  const mainEntity = useMainEntity();
  const segments = useAppSelector(allSegmentsAndSearchTermListsSelector);

  const entityType = mainEntity?.type;
  const entityId = mainEntity?.id;

  // Not a segment, so this check doesn't apply
  if (entityType !== 'segment' && entityType !== 'searchtermlist') {
    return true;
  }

  // Check if the segment exists in the loaded segments
  return segments.some((seg) => seg.id === entityId);
};

export const useCategoryIds = (): number[] => {
  const categoryIds = useAppSelector(categoryIdsSelector);
  const categoriesByRetailerId = useAppSelector(categoriesByRetailerIdSelector);
  const mainEntity = useMainEntity();

  const entityCategoryIds = mainEntity?.categoryIds ?? [];
  const entityType = mainEntity?.type;

  if (shouldShowNewAtlas()) {
    return categoriesByRetailerId;
  }

  if (entityType === 'segment') {
    return categoryIds;
  }

  return entityCategoryIds?.filter((id) => categoryIds.includes(id));
};

export const useTimePeriods = () => {
  const mainTimePeriod = useAppSelector((state) => state.mainTimePeriod);
  const comparisonTimePeriod = useAppSelector((state) => state.comparisonTimePeriod);

  return { mainTimePeriod, comparisonTimePeriod };
};

export const useCategoryCount = () => useAppSelector((state) => state.categories?.length);

/**
 * Returns true if the current retailer is an Amazon retailer but NOT Amazon US
 */
export const useIsNonUSAmazonRetailer = () => {
  const retailerId = useRetailerId();
  const isAmazon = AMAZON_RETAILER_IDS.includes(retailerId);
  const isAmazonUS = retailerId === '1';
  return isAmazon && !isAmazonUS;
};

const US_RETAILER_IDS = ['1', '2', '3', '4', '6', '9', '18', '43', '48', '49', '63', '73', '91', '92', '94', '116'];

/**
 * Returns true if the current retailer is NOT a US retailer
 */
export const useIsNonUSRetailer = () => {
  const retailerId = useRetailerId();
  return !US_RETAILER_IDS.includes(retailerId);
};
